import React, { useEffect, useState, useMemo } from "react";
import CountUp from "react-countup";
import axios from "axios";
import { Link, NavLink, useOutletContext, useParams } from "react-router-dom";
import { currency_converter_inr } from "../../../utils/currencyUtils";
import { MaterialReactTable } from "material-react-table";
import { decodeHtml } from "../../../utils/decodeHtml";
import { colors } from "@mui/material";

function ClientOriginAnalysis() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const PINE_BASE_URL = process.env.REACT_APP_PINE_BASE_URL;
  const [data, setData] = useState([]);
  const [options, setOptions] = useState([]);
  const {setGlobalLoading, token } = useOutletContext();
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [regionBreakup, setRegionBreakup] = useState([]);

  const today = new Date();
  const currentFY =
      today.getMonth() + 1 >= 4
        ? `${today.getFullYear()}-${today.getFullYear() + 1}`
        : `${today.getFullYear() - 1}-${today.getFullYear()}`;
  
  const [selectedYear, setSelectedYear] = useState(currentFY);

  const formatNumber = (num) => {
    if (!num) return 0;
    return new Intl.NumberFormat("en-IN").format(num);
    };
  async function fetchOptions() {
    setGlobalLoading(true);
    try {
        const response = await axios.get(
        `${API_BASE_URL}/reports/get_origin_analysis/`,
        {
            params: { year: selectedYear },
            headers: { Authorization: `Bearer ${token}` },
        }
        );
        setOptions(response.data.origin_analysis || []);
        if (response.data.origin_analysis.length > 0) {
            const firstOrigin = response.data.origin_analysis[0];
            setSelectedOrigin(firstOrigin);
            fetchRegionBreakup(firstOrigin.country_id);
        }
        
        const d = response.data;
        if (response.status === 200) {
            setData({
            ...d,
            });
        }
    } catch (error) {
        console.error("API Error:", error);
        // setoptions([]);
    } finally {
        setGlobalLoading(false); 
    }
  }

  useEffect(() => {
    if (token && selectedYear) {
        fetchOptions();
    }
  }, [token, selectedYear]); 

  const fetchRegionBreakup = async (countryId) => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/reports/get_region_breakup`,
                {
                    params: {
                        year: selectedYear,
                        country_id: countryId,
                    },
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setRegionBreakup(response.data.region_breakup || []);
        } catch (err) {
            console.log(err);
        }
    };
  
  const financialYears = Array.from({ length: 5 }, (_, i) => {
  const start = new Date().getFullYear() - i;
  const end = start + 1;
    return {
      label: `${start}-${end}`,
      value: `${start}-${end}`,
    };
  }); 
  const totalRevenue = useMemo(() => {
    return options.reduce((sum, item) => sum + Number(item.revenue || 0), 0);
    }, [options]);

  const regionTotals = useMemo(() => {
    return regionBreakup.reduce(
        (acc, row) => {
        acc.Americas += Number(row.Americas || 0);
        acc.India += Number(row.India || 0);
        acc.NorthAsia += Number(row.NorthAsia || 0);
        acc.SEA += Number(row.SEA || 0);
        acc.MEA += Number(row.MEA || 0);
        acc.Europe += Number(row.Europe || 0);
        acc.ANZ += Number(row.ANZ || 0);
        acc.GrandTotal += Number(row.GrandTotal || 0);
        return acc;
        },
        {
        Americas: 0,
        India: 0,
        NorthAsia: 0,
        SEA: 0,
        MEA: 0,
        Europe: 0,
        ANZ: 0,
        GrandTotal: 0,
        }
    );
    }, [regionBreakup]);  

    return (
        <>
        <style>
        {`
        .table {
            font-size: 12px;
            margin-bottom: 0;
          }
          .table th,
          .table td {
            padding: 4px 8px !important;
            white-space: nowrap;
            vertical-align: middle;
            line-height: 1.2;
          }
          .table-responsive {
            overflow: auto;
            max-height: 700px;
          }
          /* Header */
          thead th {
            position: sticky;
            top: 0;
            background: #f5f5f5 !important;
            z-index: 20;
            font-weight: 600;
          }
          /* 1st column - Unit Head */
          .sticky-col-1 {
            position: sticky;
            left: 0;
            background: #fff;
            z-index: 15;
            min-width: 100px;
          }
          /* 2nd column - Generated / Net Add */
          .sticky-col-2 {
            position: sticky;
            left: 100px;
            background: #fff;
            z-index: 15;
            min-width: 100px;
          }
          /* 3rd column - Revenue Total */
          .sticky-col-3 {
            position: sticky;
            left: 200px;
            background: #fff;
            z-index: 15;
            min-width: 140px;
          }
          /* Sticky header + sticky columns */
          thead .sticky-col-1,
          thead .sticky-col-2,
          thead .sticky-col-3 {
            background: #f5f5f5 !important;
            z-index: 25;
          }
          /* Optional border */
          .sticky-col-1,
          .sticky-col-2,
          .sticky-col-3 {
            box-shadow: 1px 0 0 #ddd;
          }
        .chart-box {
              min-height: 250px; /* adjust as needed */
              display: flex;
              flex-direction: column;
          } 
        .client-column {
            width: 70%;
            white-space: normal !important;
            word-break: break-word;
            }

        .amount-column {
            width: 30%;
            white-space: nowrap;
            } 
        .sticky-summary td {
            position: sticky;
            top: 22px;      /* Height of the header */
            background: #e9ecef;
            z-index: 20;
            font-weight: bold;
        }      
        `}
        </style>  
  
        <div className="card shadow-sm mb-4">
        <div className="card-body">
            <div className="card-header pb-2 d-flex align-items-center justify-content-between flex-wrap">
                <h5 className="mb-0">
                    Origin Analysis ({selectedYear})
                </h5>
                <div style={{ width: "150px" }}>
                    <select
                    className="form-select"
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(e.target.value)}
                    >
                    {financialYears.map((fy) => (
                        <option key={fy.value} value={fy.value}>
                        {fy.label}
                        </option>
                    ))}
                    </select>
                </div>
            </div>
            <div className="row">
                <div className="col-md-3 col-xl-3 col-xxl-3">
                    <div className="table-responsive" style={{ maxHeight: "700px" }}>
                    <table className="table table-bordered table-hover">
                        <thead className="table-dark">
                            <tr>
                            <th className="sticky-col-1">Client Origin</th>
                            <th className="sticky-col-2 text-end">Revenue</th>
                            </tr>
                        </thead>

                        <tbody>
                            {options.length > 0 ? (
                            options.map((item, index) => (
                                <tr key={index}>
                                <td className={`sticky-col-1 ${
                                        selectedOrigin?.country_id === item.country_id
                                            ? "table-primary fw-bold"
                                            : ""
                                    }`}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => {
                                        setSelectedOrigin(item);
                                        fetchRegionBreakup(item.country_id);
                                    }}
                                >
                                    {item.client_origin}
                                </td>
                                <td className="sticky-col-2 text-end">
                                    {formatNumber(item.revenue)}
                                </td>
                                </tr>
                            ))
                            ) : (
                            <tr>
                                <td colSpan="2" className="text-center">
                                No Data Found
                                </td>
                            </tr>
                            )}
                        </tbody>

                        <tfoot className="table-secondary">
                            <tr>
                            <th>Total</th>
                            <th className="text-end">
                                {formatNumber(totalRevenue)}
                            </th>
                            </tr>
                        </tfoot>
                        </table>
                    </div>        
                </div>
                <div className="col-md-9 col-xl-9 col-xxl-9">
                    <div className="table-responsive" style={{ maxHeight: "800px" }}>
                    <table className="table table-bordered table-hover">
                        <thead className="table-dark">
                            <tr>
                                <th className="sticky-col-1">Client Origin</th>
                                <th className="text-end">Americas</th>
                                <th className="text-end">India</th>
                                <th className="text-end">North Asia</th>
                                <th className="text-end">SEA</th>
                                <th className="text-end">MEA</th>
                                <th className="text-end">Europe</th>
                                <th className="text-end">ANZ</th>
                                <th className="text-end">Grand Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {/* Total Row */}
                            <tr className="sticky-summary">
                                <td>{selectedOrigin?.client_origin}</td>
                                <td className="text-end">{formatNumber(regionTotals.Americas)}</td>
                                <td className="text-end">{formatNumber(regionTotals.India)}</td>
                                <td className="text-end">{formatNumber(regionTotals.NorthAsia)}</td>
                                <td className="text-end">{formatNumber(regionTotals.SEA)}</td>
                                <td className="text-end">{formatNumber(regionTotals.MEA)}</td>
                                <td className="text-end">{formatNumber(regionTotals.Europe)}</td>
                                <td className="text-end">{formatNumber(regionTotals.ANZ)}</td>
                                <td className="text-end fw-bold">
                                    {formatNumber(regionTotals.GrandTotal)}
                                </td>
                            </tr>
                        </tbody>    
                            {/* Company Rows */}
                        <tbody>    
                            {regionBreakup.map((row, index) => (
                                <tr key={index}>
                                    <td>{row.company_name}</td>
                                    <td className="text-end">{formatNumber(row.Americas)}</td>
                                    <td className="text-end">{formatNumber(row.India)}</td>
                                    <td className="text-end">{formatNumber(row.NorthAsia)}</td>
                                    <td className="text-end">{formatNumber(row.SEA)}</td>
                                    <td className="text-end">{formatNumber(row.MEA)}</td>
                                    <td className="text-end">{formatNumber(row.Europe)}</td>
                                    <td className="text-end">{formatNumber(row.ANZ)}</td>
                                    <td className="text-end fw-bold">
                                        {formatNumber(row.GrandTotal)}
                                    </td>
                                </tr>
                            ))}

                        </tbody>    
                    </table>
                    </div> 
                </div>
            </div>

        </div>
        </div>

        </>
    );
}

export default ClientOriginAnalysis;